import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "components/AppIcon";
import toast from "react-hot-toast";

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
];

const STATUS_CONFIG = {
  draft:     { label: "Draft",     color: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400" },
  issued:    { label: "Issued",    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  submitted: { label: "Submitted to ZATCA", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" },
};

const CreditNotesTable = ({ creditNotes, onEdit, onDelete }) => {
  if (!creditNotes.length) return null;

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-visible">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              {["Date", "Invoice #", "Credit Note #", "Client", "Amount (SR)", "VAT (SR)", "Status", "Actions"].map((h) => (
                <th key={h} className="px-5 py-4 text-left text-sm font-medium text-foreground whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {creditNotes.map((note, index) => (
              <CreditNoteRow
                key={note.id}
                note={note}
                index={index}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CreditNoteRow = ({ note, index, onEdit, onDelete }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isSubmitted = note.status === "submitted";
  const statusConfig = STATUS_CONFIG[note.status] || STATUS_CONFIG.draft;

  const initials = (note.clientName || "?")
    .split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];

  // VAT = amount * taxRate / 100
  const vatAmount = parseFloat(note.amount || 0) * (parseFloat(note.taxRate || 15) / 100);

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: index * 0.04 }}
        className="hover:bg-muted/50 transition-colors"
      >
        {/* Date */}
        <td className="px-5 py-4 text-sm text-foreground whitespace-nowrap">
          {note.date
            ? new Date(note.date).toLocaleDateString("en-SA", { year: "numeric", month: "short", day: "numeric" })
            : "—"}
        </td>

        {/* Invoice # */}
        <td className="px-5 py-4">
          <span className="font-mono text-sm text-primary font-medium">
            {note.invoiceNumber || "—"}
          </span>
        </td>

        {/* Credit Note # */}
        <td className="px-5 py-4">
          <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded text-foreground">
            {note.creditNoteNumber || "—"}
          </span>
        </td>

        {/* Client */}
        <td className="px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${avatarColor}`}>
              {initials}
            </div>
            <span className="text-sm font-medium text-foreground">{note.clientName || "—"}</span>
          </div>
        </td>

        {/* Amount */}
        <td className="px-5 py-4">
          <span className="font-semibold text-foreground">
            {parseFloat(note.amount || 0).toLocaleString("en-SA", { minimumFractionDigits: 2 })}
          </span>
        </td>

        {/* VAT */}
        <td className="px-5 py-4">
          <span className="text-sm text-muted-foreground">
            {vatAmount.toLocaleString("en-SA", { minimumFractionDigits: 2 })}
          </span>
        </td>

        {/* Status */}
        <td className="px-5 py-4">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusConfig.color}`}>
            {isSubmitted && <Icon name="CheckCircle2" size={11} />}
            {statusConfig.label}
          </span>
        </td>

        {/* Actions */}
        <td className="px-5 py-4">
          <div className="flex items-center gap-1">
            <ActionBtn
              icon="Edit3" label="Edit"
              color="text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              onClick={() => onEdit(note)}
            />
            <ActionBtn
              icon="Download" label="Download"
              color="text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20"
              onClick={() => toast("Download coming soon")}
            />
            {!isSubmitted ? (
              <ActionBtn
                icon="Trash2" label="Delete"
                color="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={() => setShowDeleteConfirm(true)}
              />
            ) : (
              <div className="relative group">
                <button disabled className="p-1.5 rounded-lg text-muted-foreground/30 cursor-not-allowed">
                  <Icon name="Trash2" size={14} />
                </button>
                <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 border border-border">
                  Cannot delete — submitted to ZATCA
                </div>
              </div>
            )}
          </div>
        </td>
      </motion.tr>

      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <td colSpan={8} className="px-5 py-3 bg-red-50 dark:bg-red-900/10 border-b border-red-200 dark:border-red-800">
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                  Delete credit note <strong>{note.creditNoteNumber}</strong> for <strong>{note.clientName}</strong>? This cannot be undone.
                </span>
                <div className="flex gap-2 ml-4 flex-shrink-0">
                  <button onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-border text-foreground hover:bg-muted transition-colors">
                    Cancel
                  </button>
                  <button onClick={() => { onDelete(note.id); setShowDeleteConfirm(false); }}
                    className="px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors">
                    Delete
                  </button>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
};

const ActionBtn = ({ icon, label, color, onClick }) => (
  <div className="relative group">
    <button onClick={onClick} className={`p-1.5 rounded-lg transition-colors ${color}`}>
      <Icon name={icon} size={14} />
    </button>
    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 border border-border">
      {label}
    </div>
  </div>
);

export default CreditNotesTable;